<script lang="ts">
export default {
  name: "ToolItem",
};
</script>
<script setup lang="ts">
import BaseIcon from "@/components/Base/Icon/index.vue";
import BaseMediaItem from "@/components/Base/MediaItem.vue";
import { type MediaItem } from "@/components/Base/MediaItem.vue";
import type { Tool } from "@/types/tools";

type Props = {
  tool: Tool;
};

const { getInfoUrl, getClientPath } = useToolRouting();

const userStore = useUserStore();

const props = withDefaults(defineProps<Props>(), {});

const { t } = useI18n();

const item = computed(() => {
  const isLoggedIn = userStore.isAuthenticated && !userStore.isVisitor;

  const item: MediaItem = {
    id: props.tool.namespace,
    title: props.tool.name || t(`apps.${props.tool.namespace}.name`),
    description: t(`apps.${props.tool.namespace}.description`),
    path: isLoggedIn
      ? getClientPath(props.tool)
      : (getInfoUrl(props.tool.id) as string),
    dataTestId: `link-${props.tool.namespace}`,
    experimental: props.tool.experimental,
    new: props.tool.new,
  };
  return item;
});

const enableAiBadge = computed(() => {
  return isAiEnabledTool(props.tool.namespace);
});

const color = computed(() => {
  return props.tool.color || t(`apps.${props.tool.namespace}.color`);
});

const icon = computed(() => {
  return props.tool.icon || t(`apps.${props.tool.namespace}.icon`);
});
</script>

<template>
  <BaseMediaItem :item="item">
    <template #title>
      <div class="d-flex align-items-center">
        {{ item.title }}

        <BaseIcon
          v-if="enableAiBadge"
          v-tooltip="t('labels.withAi')"
          size="sm"
          class="robot-icon ms-1"
          icon-name="robot"
          :circle="true"
          bg-color="var(--color-tool-ki)"
        />
      </div>
    </template>
    <template #media>
      <div class="media-item__icon" :style="`background-color: ${color}`">
        <i :class="`bi ${icon}`"></i>
      </div>
    </template>
  </BaseMediaItem>
</template>

<style lang="scss" scoped>
.media-item__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  flex: none;
  width: 64px;
  height: 64px;
  border-radius: $border-radius;
  font-size: 1.2rem;

  > i {
    font-size: 1.6rem;
    filter: brightness(55%);
  }

  @include media-breakpoint-down(md) {
    width: 30px;
    height: 30px;

    > i {
      font-size: 1rem;
    }
  }
}

.robot-icon {
  z-index: 2; // needed for the tooltip to work
}
</style>
